import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Axios from 'axios';
import { isCacheValid, getCacheKey } from '../cacheUtils';

export const fetchWorkspaces = createAsyncThunk(
  'workspaces/fetchWorkspaces',
  async (filters = {}, { getState, rejectWithValue }) => {
    const state = getState().workspaces;
    const cacheKey = getCacheKey(filters);

    if (isCacheValid(state.lastFetched[cacheKey])) {
      return { data: state.data[cacheKey], cacheKey, cached: true };
    }

    try {
      const params = new URLSearchParams();
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.search) params.append('search', filters.search);
      if (filters.plan) params.append('plan', filters.plan);
      if (filters.status) params.append('status', filters.status);
      if (filters.sort) params.append('sort', filters.sort);
      if (filters.order) params.append('order', filters.order);

      const response = await Axios.get(`/api/dashboard/workspaces?${params.toString()}`);
      return { data: response.data.data, cacheKey, cached: false };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch workspaces');
    }
  }
);

export const deactivateWorkspace = createAsyncThunk(
  'workspaces/deactivate',
  async (workspaceId, { rejectWithValue }) => {
    try {
      const response = await Axios.post(`/api/dashboard/workspaces/${workspaceId}/deactivate`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to deactivate workspace');
    }
  }
);

export const activateWorkspace = createAsyncThunk(
  'workspaces/activate',
  async (workspaceId, { rejectWithValue }) => {
    try {
      const response = await Axios.post(`/api/dashboard/workspaces/${workspaceId}/activate`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to activate workspace');
    }
  }
);

export const exportAllWorkspaces = createAsyncThunk(
  'workspaces/exportAll',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (filters.plan) params.append('plan', filters.plan);
      if (filters.status) params.append('status', filters.status);

      const response = await Axios.get(`/api/dashboard/workspaces-export?${params.toString()}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `all-workspaces-${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return true;
    } catch (err) {
      return rejectWithValue('Failed to export workspaces data');
    }
  }
);

const workspacesSlice = createSlice({
  name: 'workspaces',
  initialState: {
    data: {},
    loading: false,
    mutationLoading: false,
    error: null,
    lastFetched: {},
  },
  reducers: {
    invalidateCache: (state) => {
      state.lastFetched = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkspaces.pending, (state, action) => {
        const cacheKey = getCacheKey(action.meta.arg);
        if (!state.data[cacheKey]) {
          state.loading = true;
        }
      })
      .addCase(fetchWorkspaces.fulfilled, (state, action) => {
        const { data, cacheKey, cached } = action.payload;
        if (!cached) {
          state.data[cacheKey] = data;
          state.lastFetched[cacheKey] = Date.now();
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchWorkspaces.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deactivateWorkspace.pending, (state) => {
        state.mutationLoading = true;
      })
      .addCase(deactivateWorkspace.fulfilled, (state) => {
        state.mutationLoading = false;
        state.lastFetched = {}; // invalidate cache
      })
      .addCase(deactivateWorkspace.rejected, (state, action) => {
        state.mutationLoading = false;
        state.error = action.payload;
      })
      .addCase(activateWorkspace.pending, (state) => {
        state.mutationLoading = true;
      })
      .addCase(activateWorkspace.fulfilled, (state) => {
        state.mutationLoading = false;
        state.lastFetched = {};
      })
      .addCase(activateWorkspace.rejected, (state, action) => {
        state.mutationLoading = false;
        state.error = action.payload;
      });
  },
});

export const { invalidateCache: invalidateWorkspacesCache } = workspacesSlice.actions;
export default workspacesSlice.reducer;